/**
 * @file Channel data managed via TanStack Query.
 *
 * Provides query options and mutations for channel management.
 * Channels represent connected social media accounts for profiles.
 */

import type { QueryClient } from "@tanstack/react-query";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { RouterOutput } from "@repo/api/types";
import { changeLogKeys } from "./change-log";
import { termsKeys } from "./terms";
import { trpcClient } from "../trpc";

// ============================================================================
// Query Keys
// ============================================================================

export const channelKeys = {
  all: ["channel"] as const,
  lists: () => [...channelKeys.all, "list"] as const,
  listByProfile: (organizationId: string, profileId: string) =>
    [...channelKeys.lists(), organizationId, profileId] as const,
  legacy: (organizationId: string, profileId: string) =>
    [...channelKeys.all, "legacy", organizationId, profileId] as const,
  details: () => [...channelKeys.all, "detail"] as const,
  detail: (organizationId: string, channelId: string) =>
    [...channelKeys.details(), organizationId, channelId] as const,
  socialStats: (organizationId: string, profileId: string) =>
    [...channelKeys.all, "socialStats", organizationId, profileId] as const,
};

// Per-platform social stats for the portfolio (EPD-10113), served by
// channel.getSocialStats (EPD-10112).
export type SocialStats = RouterOutput["channel"]["getSocialStats"];
export type SocialStatsEntry = SocialStats[number];

// ============================================================================
// Types
// ============================================================================

export type Platform =
  | "youtube"
  | "tiktok"
  | "instagram"
  | "twitter"
  | "facebook"
  | "twitch"
  | "linkedin"
  | "pinterest";

export type ChannelType = "creator" | "business";

/**
 * LocalStorage key for storing the return URL after OAuth callback.
 * Set before initiating OAuth, read and cleared by the callback page.
 * Uses localStorage (not sessionStorage) to survive iOS Safari page reloads.
 */
export const OAUTH_RETURN_URL_KEY = "oauth_return_url";

/**
 * LocalStorage key (namespaced by OAuth state) for where to send the creator
 * when the OAuth flow fails or is cancelled. When set, the callback page
 * forwards errors there (with a `message` param) instead of showing its own
 * error card, so flows like the partner connect page can offer a retry.
 */
export const OAUTH_ERROR_RETURN_URL_KEY = "oauth_error_return_url";

export type AuthStatus =
  | "pending"
  | "connected"
  | "expired"
  | "revoked"
  | "failed";

export interface Channel {
  id: string;
  profileId: string;
  platform: Platform;
  channelType: ChannelType;
  platformAccountId: string;
  handle: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  profileUrl: string | null;
  followerCount: string | null;
  createdAt: Date;
  updatedAt: Date;
  credential?: {
    authStatus: AuthStatus;
    tokenExpiresAt: Date | null;
    lastAuthAt: Date | null;
    lastRefreshAt: Date | null;
    refreshFailCount: number;
    lastAuthError: string | null;
  } | null;
}

export interface LegacyChannel {
  platform: Platform;
  handle: string | null;
  legacyMetadataId: string;
  isPrimary: boolean;
  isConnected: boolean;
}

// ============================================================================
// Query Options
// ============================================================================

/**
 * Query options for fetching channels by profile ID.
 */
export function channelListQueryOptions(
  organizationId: string,
  profileId: string,
) {
  return queryOptions({
    queryKey: channelKeys.listByProfile(organizationId, profileId),
    queryFn: () => trpcClient.channel.list.query({ organizationId, profileId }),
    enabled: !!profileId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Query options for fetching legacy social accounts for a profile.
 * Uses a longer stale time since legacy data rarely changes.
 */
export function legacyChannelListQueryOptions(
  organizationId: string,
  profileId: string,
) {
  return queryOptions({
    queryKey: channelKeys.legacy(organizationId, profileId),
    queryFn: () =>
      trpcClient.channel.listLegacy.query({ organizationId, profileId }),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Query options for fetching a specific channel.
 */
export function channelQueryOptions(organizationId: string, channelId: string) {
  return queryOptions({
    queryKey: channelKeys.detail(organizationId, channelId),
    queryFn: () => trpcClient.channel.get.query({ organizationId, channelId }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry(failureCount, error) {
      const status = (error as { data?: { httpStatus?: number } })?.data
        ?.httpStatus;
      if (status === 404 || status === 403) return false;
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook for fetching channels by profile ID.
 */
export function useChannelListQuery(organizationId: string, profileId: string) {
  return useQuery(channelListQueryOptions(organizationId, profileId));
}

/**
 * Hook for fetching a specific channel.
 */
export function useChannelQuery(organizationId: string, channelId: string) {
  return useQuery(channelQueryOptions(organizationId, channelId));
}

/**
 * Hook for fetching legacy social accounts for a profile.
 */
export function useLegacyChannelListQuery(
  organizationId: string,
  profileId: string,
) {
  return useQuery(legacyChannelListQueryOptions(organizationId, profileId));
}

/**
 * Hook for fetching per-platform social stats for a profile (EPD-10113).
 * Fetched live (cached) from the legacy system; disabled until ids are known.
 */
export function useSocialStatsQuery(organizationId: string, profileId: string) {
  return useQuery({
    queryKey: channelKeys.socialStats(organizationId, profileId),
    queryFn: () =>
      trpcClient.channel.getSocialStats.query({ organizationId, profileId }),
    enabled: Boolean(organizationId) && Boolean(profileId),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook for generating OAuth connect URL.
 * Returns the authorization URL to redirect the user to.
 */
export function useConnectChannel() {
  return useMutation({
    mutationFn: (data: {
      organizationId: string;
      profileId: string;
      platform: Platform;
      channelType?: ChannelType;
      scopes?: string[];
    }) => trpcClient.channel.connectUrl.mutate(data),
  });
}

// Sentinel for detecting expired OAuth state errors from the backend
export const STATE_EXPIRED_TOKEN = "state has expired";

// User-facing message shown when OAuth state expires
export const STATE_EXPIRED_MESSAGE =
  "Your authorization session has expired. Please go back and try connecting your account again.";

/**
 * Hook for handling OAuth callback.
 * Called after user returns from OAuth provider.
 */
export function useChannelCallback(
  organizationId?: string,
  profileId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    // OAuth authorization codes are single-use — retrying with the same code
    // always fails ("invalid_grant" / "code already used"), so disable retries.
    retry: false,
    mutationFn: (data: {
      organizationId: string;
      code: string;
      state: string;
    }) => trpcClient.channel.callback.mutate(data),
    onSuccess: (_, variables) => {
      // Invalidate all channel lists since we don't know which profile
      queryClient.invalidateQueries({ queryKey: channelKeys.lists() });
      // Invalidate legacy queries to update isConnected status
      if (profileId && organizationId) {
        queryClient.invalidateQueries({
          queryKey: channelKeys.legacy(organizationId, profileId),
        });
      }
      // Invalidate change log for the profile if known
      // Use organizationId from hook arg or fall back to mutation variables
      const orgId = organizationId ?? variables.organizationId;
      if (profileId && orgId) {
        queryClient.invalidateQueries({
          queryKey: changeLogKeys.profile(orgId, profileId),
        });
      }
      // Invalidate terms queries so sign-brief page reflects the newly connected channel
      queryClient.invalidateQueries({ queryKey: termsKeys.all });
    },
  });
}

/**
 * Hook for connecting a specific YouTube channel selected from the picker.
 * Called after useChannelCallback returns type: 'channel_selection_required'.
 */
export function useYoutubeChannelConnect(
  organizationId?: string,
  profileId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      organizationId: string;
      tokenBundle: string;
      channelId: string;
    }) => trpcClient.channel.youtubeChannelConnect.mutate(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: channelKeys.lists() });
      if (profileId && organizationId) {
        queryClient.invalidateQueries({
          queryKey: channelKeys.legacy(organizationId, profileId),
        });
      }
      const orgId = organizationId ?? variables.organizationId;
      if (profileId && orgId) {
        queryClient.invalidateQueries({
          queryKey: changeLogKeys.profile(orgId, profileId),
        });
      }
      queryClient.invalidateQueries({ queryKey: termsKeys.all });
    },
  });
}

/**
 * Hook for disconnecting a channel.
 */
export function useDisconnectChannel(
  organizationId?: string,
  profileId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { organizationId: string; channelId: string }) =>
      trpcClient.channel.disconnect.mutate(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: channelKeys.all });
      // Invalidate change log for the profile if known
      // Use organizationId from hook arg or fall back to mutation variables
      const orgId = organizationId ?? variables.organizationId;
      if (profileId && orgId) {
        queryClient.invalidateQueries({
          queryKey: changeLogKeys.profile(orgId, profileId),
        });
      }
    },
  });
}

/**
 * Hook for refreshing channel tokens.
 */
export function useRefreshChannel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { organizationId: string; channelId: string }) =>
      trpcClient.channel.refresh.mutate(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: channelKeys.detail(
          variables.organizationId,
          variables.channelId,
        ),
      });
      queryClient.invalidateQueries({ queryKey: channelKeys.lists() });
    },
  });
}

// ============================================================================
// Cache Utilities
// ============================================================================

/**
 * Prefetch channels for a profile.
 * Useful in route loaders.
 */
export async function prefetchChannelList(
  queryClient: QueryClient,
  organizationId: string,
  profileId: string,
) {
  return queryClient.fetchQuery(
    channelListQueryOptions(organizationId, profileId),
  );
}

/**
 * Invalidate all channel queries.
 */
export function invalidateChannelQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: channelKeys.all });
}

/**
 * Invalidate channels for a specific profile.
 */
export function invalidateProfileChannels(
  queryClient: QueryClient,
  organizationId: string,
  profileId: string,
) {
  return queryClient.invalidateQueries({
    queryKey: channelKeys.listByProfile(organizationId, profileId),
  });
}
